// A questionnaire create refused because its create key was used before, and what the researcher is
// told about it. The notice is kept here, outside any component: the editor that sent the create may
// have closed by the time the answer comes, and a live project's publish has no editor at all.
import type {
  QuestionnaireDraftKey,
  SavedQuestionnaireSummary,
} from "../../model/questionnaire_draft";
import type { BaseRequestResult } from "../request";
import { SessionScope, isSessionScopeCurrent, onSessionScopeEnd } from "../auth/session_scope";

export type QuestionnaireCreateConflict =
  | { kind: "already_created"; taskId: string }
  | { kind: "in_progress" };

// A create refused because its key was used before: that create went through as `taskId`, or is
// still going through. Null for any other answer.
export const questionnaireCreateConflictOf = (
  result: Pick<BaseRequestResult, "statusCode" | "payload"> | null | undefined,
): QuestionnaireCreateConflict | null => {
  if (result?.statusCode !== 409) return null;
  const payload = result.payload as {
    errorType?: unknown;
    clientData?: { taskId?: unknown } | null;
  } | null;
  const taskId = payload?.clientData?.taskId;
  if (payload?.errorType === "QUESTIONNAIRE_ALREADY_CREATED" && typeof taskId === "string") {
    return { kind: "already_created", taskId };
  }
  if (payload?.errorType === "QUESTIONNAIRE_CREATE_IN_PROGRESS") return { kind: "in_progress" };
  return null;
};

export type QuestionnaireCreateConflictNotice =
  // `copyKey`: the recovery copy the create came from, kept for review; `savedTask`: the questionnaire
  // the first create made, null when the method's tasks could not be fetched
  | {
      kind: "already_created";
      scope: SessionScope;
      copyKey: QuestionnaireDraftKey | null;
      savedTask: SavedQuestionnaireSummary | null;
    }
  | { kind: "in_progress"; scope: SessionScope };

let notice: QuestionnaireCreateConflictNotice | null = null;
const listeners = new Set<() => void>();

const setNotice = (next: QuestionnaireCreateConflictNotice | null) => {
  notice = next;
  listeners.forEach((listener) => listener());
};

// shown only while the session the create was sent in is still the one signed in
export const showQuestionnaireCreateConflict = (next: QuestionnaireCreateConflictNotice): void => {
  if (isSessionScopeCurrent(next.scope)) setNotice(next);
};

export const dismissQuestionnaireCreateConflict = (): void => {
  if (notice) setNotice(null);
};

export const getQuestionnaireCreateConflict = (): QuestionnaireCreateConflictNotice | null =>
  notice;

export const subscribeQuestionnaireCreateConflict = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

onSessionScopeEnd((ended) => {
  if (notice?.scope.generation === ended.generation) setNotice(null);
});
